import { useCompany } from '../../hooks/useCompany';
import { usePromotion } from '../../hooks/usePromotion';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PromotionStatusOption } from '../../hooks/usePromotion';
import { ComputedPromotionStatus } from '../../stores/types/promotions.interface';
import { getPromotionStatusLabel } from '../../utils/promotion.util';
import PromotionFilters from './PromotionFilters.component';
import PromotionTable from './PromotionTable.component';
import { useFetchPromotionsQuery } from '../../services/promotion.api';

const Promotions = () => {
  const { t } = useTranslation();

  const { companyId } = useCompany();

  const promotion = usePromotion(companyId);
  const { searches, status } = promotion;

  const { data, isLoading } = useFetchPromotionsQuery(promotion.queryArgs!, {
    skip: !promotion.queryArgs,
  });

  const statusOptions: PromotionStatusOption[] = useMemo(() => {
    return Object.values(ComputedPromotionStatus).map((status) => {
      return {
        id: status,
        label: t(getPromotionStatusLabel(status)),
        selected: promotion.status.includes(status),
        indeterminate: false,
        children: [],
      };
    });
  }, [t, promotion.status]);

  const handleStatusChange = useCallback(
    (items: PromotionStatusOption[]) => {
      const selectedStatuses = items.filter((item) => item.selected).map((item) => item.id);

      promotion.setStatuses(selectedStatuses);
    },
    [promotion],
  );

  const handleRemoveStatus = useCallback(
    (statusToRemove: ComputedPromotionStatus) => {
      promotion.setStatuses(promotion.status.filter((s) => s !== statusToRemove));
    },
    [promotion],
  );

  const filteredPromotions = useMemo(() => {
    if (!data?.entities) return [];

    let result = data.entities;

    if (searches.length > 0) {
      result = result.filter((p) =>
        searches.some((s) => p.name.toLowerCase().includes(s.toLowerCase())),
      );
    }

    if (status.length > 0) {
      result = result.filter((p) => status.includes(p.status));
    }

    return result;
  }, [data?.entities, searches, status]);

  return (
    <>
      <PromotionFilters
        searches={promotion.searches}
        statusOptions={statusOptions}
        status={promotion.status}
        onAddSearch={promotion.addSearch}
        onRemoveSearch={promotion.removeSearch}
        onStatusChange={handleStatusChange}
        onRemoveStatus={handleRemoveStatus}
        onClearAll={promotion.clearAll}
        numOfPills={promotion.numOfPills}
      />

      <PromotionTable data={filteredPromotions} loading={isLoading} />
    </>
  );
};

export default Promotions;
